import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, X, Check, Plus, PencilLine, ImagePlus, 
  Mail, ShieldCheck, Paperclip, FileText, CheckCircle2, 
  ArrowRight, EyeOff, AlertCircle, Building2, User as UserIcon,
  Users, Sparkles, RotateCcw, Maximize2, Minimize2
} from 'lucide-react';
import { TEAMS, getTeam } from '@/lib/teams';
import { useProblems, type ProblemDef } from '@/lib/problems';
import { useTeamFields } from '@/lib/customFields';
import { useSlaPolicy, computeDue } from '@/lib/sla';
import { derivePriority, type Module, type Ticket } from '@/lib/mock';
import type { NewTicket } from '@/lib/data';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useUsers } from '@/lib/useUsers';

const PRIORITIES = [
  { id: 'P1', label: 'P1 - Urgent', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
  { id: 'P2', label: 'P2 - High', color: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  { id: 'P3', label: 'P3 - Medium', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  { id: 'P4', label: 'P4 - Low', color: 'bg-slate-500/10 text-slate-600 border-slate-500/30' }
] as const;

const MODULES: { id: Module; label: string; desc: string }[] = [
  { id: 'Incident', label: 'Incident', desc: 'Something is broken or degraded' },
  { id: 'Service Request', label: 'Service Request', desc: 'New equipment, access, or setup' },
  { id: 'Problem', label: 'Problem', desc: 'Investigate recurring root cause' },
  { id: 'Change', label: 'Change Request', desc: 'Planned modification or update' }
];

const DEFAULT_CATEGORIES = [
  'Hardware',
  'Software',
  'Network',
  'Access & Permissions',
  'Email & Communication',
  'General Inquiry'
];

const LEVELS = ['High', 'Medium', 'Low'] as const;

export function CreateTicket({
  open,
  initialTeamId,
  onClose,
  onCreate,
  onSelectTicket
}: {
  open: boolean;
  initialTeamId?: string | null;
  onClose: () => void;
  onCreate: (d: NewTicket) => Promise<any> | any;
  onSelectTicket?: (id: string) => void;
}) {
  const { session, user } = useAuth();
  const { profiles } = useUsers();
  const navigate = useNavigate();
  
  const [teamId, setTeamId] = useState(initialTeamId ?? TEAMS[0].id);
  const [isFitToScreen, setIsFitToScreen] = useState(true);

  useEffect(() => {
    if (initialTeamId) {
      setTeamId(initialTeamId);
    }
  }, [initialTeamId]);

  const team = getTeam(teamId) ?? TEAMS[0];
  const { problems, addProblem } = useProblems(team.id, team.type);
  const { fields: teamFields } = useTeamFields(team.id);
  const { policy } = useSlaPolicy(team.id);
  const [customValues, setCustomValues] = useState<Record<string, unknown>>({});
  const setCustom = (key: string, v: unknown) => setCustomValues((p) => ({ ...p, [key]: v }));

  // Categories loaded from DB or fallback
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);

  useEffect(() => {
    let isMounted = true;
    async function loadCategories() {
      try {
        const token = session?.access_token;
        if (!token) return;
        const res = await fetch('/api/categories', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            const names = data.map((c: any) => c.name || c.category_name).filter(Boolean);
            if (isMounted && names.length > 0) {
              setCategories(Array.from(new Set([...names, ...DEFAULT_CATEGORIES])));
            }
          }
        }
      } catch (e) {
        // Fallback silently
      }
    }
    loadCategories();
    return () => { isMounted = false; };
  }, [session?.access_token]);

  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<ProblemDef | 'custom' | null>(null);
  const [subject, setSubject] = useState('');
  const [module, setModule] = useState<Module>('Incident');
  const [priority, setPriority] = useState<string>('P3');
  const [category, setCategory] = useState('Hardware');
  const [description, setDescription] = useState('');
  const [saveToCatalog, setSaveToCatalog] = useState(true);
  
  // Attachments
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  // Email notifications & recipients
  const [ccList, setCcList] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const [bccList, setBccList] = useState<string[]>([]);
  const [bccInput, setBccInput] = useState('');
  const [showBcc, setShowBcc] = useState(false);

  // Richer fields
  const [impact, setImpact] = useState('Medium');
  const [urgency, setUrgency] = useState('Medium');
  const [ci, setCi] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [requesterId, setRequesterId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Success state modal
  const [createdResult, setCreatedResult] = useState<{
    ticket: Ticket;
    notification?: any;
  } | null>(null);

  const isAgent = session?.user?.role === 'Admin' || 
                  session?.user?.role === 'Super Admin' || 
                  session?.user?.role === 'Agent' || 
                  session?.user?.role === 'Technician';

  // Logged in user profile info
  const currentUserDisplayName = user?.displayName || user?.name || session?.user?.name || 'Authorized User';
  const currentUserEmail = user?.email || session?.user?.email || 'user@rezilyens.com';

  // Dynamic requester selection (if agent picks another user)
  const selectedRequesterProfile = profiles.find(p => p.id === requesterId);
  const effectiveRequesterEmail = selectedRequesterProfile?.email || currentUserEmail;
  const effectiveRequesterDisplayName = selectedRequesterProfile?.full_name || currentUserDisplayName;

  const pickImpact = (v: string) => {
    setImpact(v);
    const p = derivePriority(v, urgency);
    if (p) setPriority(p);
  };

  const pickUrgency = (v: string) => {
    setUrgency(v);
    const p = derivePriority(impact, v);
    if (p) setPriority(p);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setPicked('custom');
    setSubject(`Issue analyzed from image: System crash`);
    setModule('Incident');
    setPriority('P1');
    setCategory('Hardware');
    setDescription(`Attached file: ${file.name}\n\nAI Analysis: The image shows a critical hardware or kernel fault. Diagnostics required.`);
    setImpact('High');
    setUrgency('High');
    setSaveToCatalog(false);
    setAttachedFiles((prev) => [...prev, file]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setAttachedFiles((prev) => [...prev, ...newFiles]);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    }
  };

  const removeFile = (idx: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const addCc = (emailToAdd: string) => {
    const clean = emailToAdd.trim().toLowerCase();
    if (clean && !ccList.includes(clean) && clean.includes('@')) {
      setCcList([...ccList, clean]);
      setCcInput('');
    }
  };

  const removeCc = (idx: number) => {
    setCcList(ccList.filter((_, i) => i !== idx));
  };

  const addBcc = (emailToAdd: string) => {
    const clean = emailToAdd.trim().toLowerCase();
    if (clean && !bccList.includes(clean) && clean.includes('@')) {
      setBccList([...bccList, clean]);
      setBccInput('');
    }
  };

  const removeBcc = (idx: number) => {
    setBccList(bccList.filter((_, i) => i !== idx));
  };

  const reset = () => {
    setPicked(null);
    setSubject('');
    setDescription('');
    setQ('');
    setPriority('P3');
    setModule('Incident');
    setCategory('Hardware');
    setImpact('Medium');
    setUrgency('Medium');
    setCi('');
    setDueAt('');
    setRequesterId('');
    setCustomValues({});
    setAttachedFiles([]);
    setCcList([]);
    setBccList([]);
    setShowBcc(false);
    setSubmitError(null);
    setCreatedResult(null);
  };

  const switchTeam = (id: string) => {
    setTeamId(id);
    reset();
  };

  const choose = (p: ProblemDef) => {
    setPicked(p);
    setSubject(p.label);
    setModule(p.module);
    setPriority(p.priority);
    setCategory(p.category);
    setSubmitError(null);
  };

  const chooseCustom = () => {
    setPicked('custom');
    if (!subject) setSubject(q.trim());
    setModule('Incident');
    setPriority('P3');
    setCategory('Hardware');
    setSubmitError(null);
  };

  const submit = async () => {
    if (!subject.trim()) {
      setSubmitError('Ticket Subject is required.');
      return;
    }
    if (isSubmitting) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (picked === 'custom' && saveToCatalog) {
        addProblem({ label: subject.trim(), module, priority: priority as ProblemDef['priority'], category });
      }

      let finalCc = [...ccList];
      if (ccInput.trim() && ccInput.includes('@')) {
        const clean = ccInput.trim().toLowerCase();
        if (!finalCc.includes(clean)) {
          finalCc.push(clean);
        }
      }

      let finalBcc = [...bccList];
      if (bccInput.trim() && bccInput.includes('@')) {
        const clean = bccInput.trim().toLowerCase();
        if (!finalBcc.includes(clean)) {
          finalBcc.push(clean);
        }
      }

      const res = await onCreate({
        module,
        subject: subject.trim(),
        priority,
        description: description.trim(),
        requesterId: requesterId || user?.userId || user?.id || undefined,
        requester: effectiveRequesterDisplayName,
        requesterEmail: effectiveRequesterEmail,
        team: team.name,
        teamId: team.id,
        category,
        ci: ci.trim() || undefined,
        impact,
        urgency,
        dueAt: dueAt ? new Date(dueAt).getTime() : undefined,
        source: isAgent ? 'Technician' : 'Web',
        attachments: attachedFiles,
        cc: finalCc,
        bcc: finalBcc,
        requestType: module,
        custom: customValues,
        ...(() => {
          const due = computeDue(policy, priority, Date.now());
          return { slaDueAt: due.resolutionDueAt, responseDueAt: due.responseDueAt };
        })(),
      });

      if (res && typeof res === 'object' && res.success === false) {
        throw new Error(res.error || 'Failed to create ticket on server.');
      }

      // If res returned ticket object, show the enterprise success modal!
      if (res && res.ticket) {
        setCreatedResult({
          ticket: res.ticket,
          notification: res.notification
        });
      } else {
        // Fallback close if result doesn't have ticket
        reset();
        onClose();
      }
    } catch (err: any) {
      console.error('Ticket submission failed:', err);
      setSubmitError(err.message || 'Failed to create ticket. Please verify your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center overflow-hidden transition-all duration-200",
      isFitToScreen ? "p-1.5 sm:p-3" : "p-3 sm:p-5"
    )}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
      
      <div className={cn(
        "relative z-10 flex flex-col overflow-hidden neo shadow-2xl border border-border bg-card transition-all duration-200",
        isFitToScreen 
          ? "w-[98vw] max-w-[1580px] h-[96vh] rounded-2xl" 
          : "w-full max-w-3xl h-[92vh] max-h-[820px] rounded-2xl md:rounded-[1.75rem]"
      )}>
        
        {/* If createdResult exists, render the Success Confirmation View */}
        {createdResult ? (
          <div className="flex flex-col items-center justify-center p-8 sm:p-12 text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 flex items-center justify-center mb-5 animate-in zoom-in-50">
              <CheckCircle2 className="h-10 w-10 drop-shadow" />
            </div>

            <span className="rounded-full bg-emerald-500/10 border border-emerald-500/25 px-3 py-1 text-xs font-semibold text-emerald-600 tracking-wide uppercase mb-2">
              Ticket Logged & Dispatched
            </span>
            <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground">
              {createdResult.ticket.ref}
            </h2>
            <p className="text-sm font-medium text-muted-foreground mt-1 max-w-lg">
              "{createdResult.ticket.subject}"
            </p>

            {/* Notification Dispatched Banner */}
            <div className="mt-6 w-full max-w-md rounded-xl border border-border bg-muted/30 p-4 text-left space-y-2.5">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-primary" /> Delivery Confirmations
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground">
                <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span>Acknowledgment email sent to <strong className="font-semibold text-foreground">{effectiveRequesterEmail}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground">
                <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span>Alert notification delivered to <strong className="font-semibold text-foreground">techsupport@pinochle.ai</strong></span>
              </div>
              {(createdResult.ticket.custom?.cc && Array.isArray(createdResult.ticket.custom.cc) && createdResult.ticket.custom.cc.length > 0) ? (
                <div className="flex items-start gap-2 text-xs text-foreground">
                  <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Carbon copy (CC) sent to: <strong className="font-semibold text-foreground">{(createdResult.ticket.custom.cc as string[]).join(', ')}</strong></span>
                </div>
              ) : ccList.length > 0 ? (
                <div className="flex items-start gap-2 text-xs text-foreground">
                  <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Carbon copy (CC) sent to: <strong className="font-semibold text-foreground">{ccList.join(', ')}</strong></span>
                </div>
              ) : null}
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => {
                  const targetRef = createdResult.ticket.ref || createdResult.ticket.id;
                  const tId = createdResult.ticket.id;
                  reset();
                  onClose();
                  if (onSelectTicket) onSelectTicket(tId);
                  navigate(`/tickets/${targetRef}`);
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-md hover:brightness-110 transition-all"
              >
                <span>View Ticket</span>
                <ArrowRight className="h-4 w-4" />
              </button>
              
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-all"
              >
                <Plus className="h-4 w-4" />
                <span>Create Another Ticket</span>
              </button>

              <button
                onClick={() => { reset(); onClose(); }}
                className="inline-flex items-center gap-2 rounded-xl border border-transparent px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-all"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-5 sm:px-6 py-3 bg-muted/10 shrink-0">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <PencilLine className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-display text-sm sm:text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
                    <span>Create Support Ticket</span>
                    {isFitToScreen && (
                      <span className="hidden md:inline-flex items-center rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] font-semibold">
                        Fit to Screen
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Submit an issue or request to the technical support team.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  onClick={() => setIsFitToScreen(!isFitToScreen)} 
                  className={cn(
                    "rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all border flex items-center gap-1.5",
                    isFitToScreen 
                      ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20" 
                      : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  title={isFitToScreen ? "Switch to Normal View" : "Fit to Screen"}
                  aria-label="Toggle Fit to Screen"
                >
                  {isFitToScreen ? (
                    <>
                      <Minimize2 className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Normal View</span>
                    </>
                  ) : (
                    <>
                      <Maximize2 className="h-3.5 w-3.5 text-primary" />
                      <span className="hidden sm:inline font-semibold text-primary">Fit to Screen</span>
                    </>
                  )}
                </button>
                <button 
                  onClick={onClose} 
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" 
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Team Based Selection */}
            <div className="flex items-center gap-2 overflow-x-auto border-b border-border px-5 sm:px-6 py-2 bg-muted/20 no-scrollbar shrink-0">
              <span className="flex-shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-primary" /> Team:
              </span>
              <div className="flex items-center gap-1.5 flex-nowrap">
                {TEAMS.map((t) => {
                  const active = t.id === teamId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => switchTeam(t.id)}
                      className={cn(
                        'flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition shadow-xs',
                        active 
                          ? 'border-transparent text-white shadow-sm ring-1 ring-white/20 font-semibold' 
                          : 'border-border/80 bg-card text-muted-foreground hover:text-foreground hover:border-border'
                      )}
                      style={active ? { background: `hsl(${t.hue} 65% 48%)` } : undefined}
                    >
                      <t.icon className="h-3.5 w-3.5 shrink-0" />
                      <span>{t.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Scrollable Form Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-4 custom-scrollbar">
              {submitError && (
                <div className="flex items-start justify-between rounded-xl border border-destructive/30 bg-destructive/10 p-3.5 text-xs text-destructive animate-in fade-in">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setSubmitError(null)} 
                    className="text-destructive/70 hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* 1. Requester Profile Information (Read-Only / Authenticated) */}
              <div className="rounded-xl border border-border bg-muted/20 p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <UserIcon className="h-3.5 w-3.5 text-primary" /> Requester Information
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                    <ShieldCheck className="h-3 w-3" /> Authenticated Session
                  </span>
                </div>
                
                {isAgent ? (
                  <div className="space-y-1 mt-1">
                    <select
                      value={requesterId}
                      onChange={(e) => setRequesterId(e.target.value)}
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">Myself: {currentUserDisplayName} ({currentUserEmail})</option>
                      {profiles.map(p => (
                        <option key={p.id} value={p.id}>{p.full_name} ({p.email})</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center text-xs">
                        {currentUserDisplayName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">{currentUserDisplayName}</div>
                        <div className="text-muted-foreground font-mono text-[11px]">{currentUserEmail}</div>
                      </div>
                    </div>
                    <span className="text-[11px] text-muted-foreground bg-background px-2 py-1 rounded border border-border">
                      Verified Requester
                    </span>
                  </div>
                )}
              </div>

              {/* 2. Choose an Issue or Compact Selected Banner */}
              {picked ? (
                <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 p-3 animate-in fade-in duration-150">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-semibold shrink-0">
                      {picked === 'custom' ? <Sparkles className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-foreground truncate">
                        {picked === 'custom' ? '✨ Custom Support Request' : picked.label}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {team.name} · {picked === 'custom' ? 'Manual details' : `${picked.module} · ${picked.priority} · ${picked.category}`}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setPicked(null); setSubject(''); }}
                    className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted shrink-0 transition"
                  >
                    <RotateCcw className="h-3 w-3 text-muted-foreground" />
                    <span>Change Issue</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" /> Choose an Issue or Create Custom Ticket
                    </label>
                    <button 
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                    >
                      <ImagePlus className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Scan Error Screenshot</span>
                    </button>
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      className="hidden" 
                      accept="image/*" 
                      onChange={handleImageUpload} 
                    />
                  </div>

                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder={`Search ${team.name} common issues or describe a new one...`}
                      className="h-9 w-full rounded-xl border border-input bg-background pl-9 pr-3 text-xs sm:text-sm outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>

                  {/* Prominent Custom Ticket Button / Card */}
                  <button
                    type="button"
                    onClick={chooseCustom}
                    className="w-full group flex items-center justify-between gap-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary p-3 text-left transition shadow-xs"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg font-bold bg-primary/20 text-primary shrink-0">
                        <Plus className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-foreground flex items-center gap-2">
                          <span>✨ Custom Ticket</span>
                          <span className="rounded-full bg-primary/20 text-primary px-2 py-0.5 text-[10px] font-semibold">
                            Custom Issue
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {q.trim() ? `Create custom ticket with title: "${q.trim()}"` : 'Select to describe a custom issue and fill in ticket details manually'}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-primary shrink-0 hidden sm:inline">
                      Select Custom Ticket →
                    </span>
                  </button>

                  {/* Common Team Issues Grid */}
                  {problems.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Common Issues — {team.name}
                      </div>
                      <div className={cn(
                        "grid gap-2",
                        isFitToScreen ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2"
                      )}>
                        {(q.trim()
                          ? problems.filter((p) => p.label.toLowerCase().includes(q.toLowerCase()) || p.category.toLowerCase().includes(q.toLowerCase()))
                          : problems
                        ).slice(0, 8).map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => choose(p)}
                            className="group relative flex items-start gap-2.5 rounded-xl border border-border/70 hover:border-primary/50 bg-card hover:bg-muted/30 p-2.5 text-left transition"
                          >
                            <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: `hsl(${team.hue} 65% 52%)` }} />
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium text-foreground leading-snug">{p.label}</div>
                              <div className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                                <span>{p.module}</span><span>·</span><span>{p.priority}</span><span>·</span><span>{p.category}</span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 4. Ticket Details Form (Only showing when Custom Ticket or issue selected) */}
              {picked && (
                <div className="space-y-4 animate-in fade-in-50 duration-200">
                  <div className="flex items-center justify-between border-b border-border pb-2.5">
                    <div className="flex items-center gap-2">
                      <PencilLine className="h-4 w-4 text-primary" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                        Ticket Details {picked === 'custom' ? '— Custom Ticket' : `— ${picked.label}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {picked === 'custom' ? (
                        <span className="rounded-full bg-primary/10 border border-primary/20 px-2.5 py-0.5 text-[10px] font-semibold text-primary">
                          Custom Ticket
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={chooseCustom}
                          className="text-[11px] text-primary hover:underline font-medium flex items-center gap-1"
                        >
                          <Plus className="h-3 w-3" /> Switch to Custom Ticket
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Subject / Title <span className="text-destructive">*</span>
                    </label>
                    <input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={picked === 'custom' ? "Brief summary of the issue (e.g. Cannot connect to Office VPN from home)" : "Ticket subject"}
                      className="h-10 w-full rounded-xl border border-input bg-background px-3.5 text-sm outline-none transition focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>

                {/* Category & Request Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Category
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Request Type
                    </label>
                    <select
                      value={module}
                      onChange={(e) => setModule(e.target.value as Module)}
                      className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      {MODULES.map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Priority Selection */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Priority
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {PRIORITIES.map((p) => {
                      const active = priority === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPriority(p.id)}
                          className={cn(
                            'flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition',
                            active ? `${p.color} ring-1 ring-current` : 'border-border text-muted-foreground hover:text-foreground bg-background'
                          )}
                        >
                          <span>{p.label}</span>
                          {active && <Check className="h-3.5 w-3.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Impact & Urgency Matrix (Collapsible or visible) */}
                <div className="rounded-xl border border-border bg-muted/10 p-3.5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Impact & Urgency
                    </span>
                    <span className="text-[11px] text-muted-foreground font-mono">
                      Derived: <strong className="text-foreground font-semibold">{priority}</strong>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground mb-1">Impact</div>
                      <div className="flex gap-1">
                        {LEVELS.map((lvl) => (
                          <button
                            key={lvl}
                            type="button"
                            onClick={() => pickImpact(lvl)}
                            className={cn(
                              'flex-1 py-1 rounded text-xs font-medium border transition',
                              impact === lvl ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background text-muted-foreground hover:text-foreground'
                            )}
                          >
                            {lvl}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground mb-1">Urgency</div>
                      <div className="flex gap-1">
                        {LEVELS.map((lvl) => (
                          <button
                            key={lvl}
                            type="button"
                            onClick={() => pickUrgency(lvl)}
                            className={cn(
                              'flex-1 py-1 rounded text-xs font-medium border transition',
                              urgency === lvl ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background text-muted-foreground hover:text-foreground'
                            )}
                          >
                            {lvl}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description Details */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Description & Details
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    placeholder="Provide full details: What happened? What steps lead to this issue? Include error codes or relevant context..."
                    className="w-full resize-y rounded-xl border border-input bg-background p-3.5 text-sm outline-none transition focus:border-primary focus:ring-1 focus:ring-primary leading-relaxed"
                  />
                </div>

                {/* 4. Attachments Upload */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Paperclip className="h-3.5 w-3.5" /> Attachments
                    </label>
                    <button
                      type="button"
                      onClick={() => attachmentInputRef.current?.click()}
                      className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Files
                    </button>
                    <input
                      type="file"
                      ref={attachmentInputRef}
                      className="hidden"
                      multiple
                      onChange={handleFileAttach}
                    />
                  </div>

                  {attachedFiles.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {attachedFiles.map((f, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded-lg border border-border bg-background text-xs">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="h-4 w-4 text-primary shrink-0" />
                            <span className="truncate font-medium text-foreground">{f.name}</span>
                            <span className="text-[10px] text-muted-foreground shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            className="text-muted-foreground hover:text-destructive p-1 rounded transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div 
                      onClick={() => attachmentInputRef.current?.click()}
                      className="border border-dashed border-border hover:border-primary/50 rounded-xl p-4 text-center cursor-pointer transition-colors bg-muted/5 hover:bg-muted/10"
                    >
                      <Paperclip className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
                      <p className="text-xs text-muted-foreground">Click to upload logs, screenshots, or documents</p>
                    </div>
                  )}
                </div>

                {/* 5. Email Notification Recipients (Section 35) */}
                <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Mail className="h-4 w-4 text-primary" /> Email Notifications & Delivery
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Automated Microsoft 365 / Graph dispatch
                    </span>
                  </div>

                  {/* To (Read-only / Prefilled) */}
                  <div>
                    <span className="text-[11px] font-semibold text-muted-foreground block mb-1">
                      To (Primary Recipients)
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground">
                        <Building2 className="h-3 w-3 text-primary" />
                        <span>techsupport@pinochle.ai</span>
                        <span className="text-[10px] text-muted-foreground">(Helpdesk)</span>
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground">
                        <UserIcon className="h-3 w-3 text-primary" />
                        <span>{effectiveRequesterEmail}</span>
                        <span className="text-[10px] text-muted-foreground">({effectiveRequesterDisplayName} - Requester)</span>
                      </span>
                    </div>
                  </div>

                  {/* CC (Carbon Copy) */}
                  <div>
                    <span className="text-[11px] font-semibold text-muted-foreground block mb-1">
                      CC (Carbon Copy)
                    </span>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={ccInput}
                          onChange={(e) => setCcInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                              e.preventDefault();
                              addCc(ccInput);
                            }
                          }}
                          placeholder="Add email and press Enter (e.g. manager@pinochle.ai)"
                          className="h-8 flex-1 rounded-lg border border-input bg-background px-3 text-xs outline-none focus:border-primary"
                        />
                        <button
                          type="button"
                          onClick={() => addCc(ccInput)}
                          className="px-2.5 py-1 text-xs font-medium rounded-lg border border-border bg-background hover:bg-muted"
                        >
                          Add CC
                        </button>
                      </div>

                      {ccList.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {ccList.map((email, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 rounded-md bg-background border border-border px-2 py-0.5 text-xs text-foreground"
                            >
                              <span>{email}</span>
                              <button
                                type="button"
                                onClick={() => removeCc(idx)}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* BCC (Blind Carbon Copy - Optional & Secure) */}
                  <div>
                    {!showBcc ? (
                      <button
                        type="button"
                        onClick={() => setShowBcc(true)}
                        className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" /> Add BCC (Confidential / Audit Delivery)
                      </button>
                    ) : (
                      <div className="space-y-2 pt-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1">
                            <EyeOff className="h-3 w-3 text-amber-500" /> BCC (Hidden Delivery)
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowBcc(false)}
                            className="text-[10px] text-muted-foreground hover:underline"
                          >
                            Hide BCC
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={bccInput}
                            onChange={(e) => setBccInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                addBcc(bccInput);
                              }
                            }}
                            placeholder="Add confidential audit email (e.g. security-audit@example.com)"
                            className="h-8 flex-1 rounded-lg border border-input bg-background px-3 text-xs outline-none focus:border-primary"
                          />
                          <button
                            type="button"
                            onClick={() => addBcc(bccInput)}
                            className="px-2.5 py-1 text-xs font-medium rounded-lg border border-border bg-background hover:bg-muted"
                          >
                            Add BCC
                          </button>
                        </div>

                        {bccList.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {bccList.map((email, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400"
                              >
                                <span>{email}</span>
                                <button
                                  type="button"
                                  onClick={() => removeBcc(idx)}
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground">
                          Note: BCC recipients will receive an encrypted copy discreetly without appearing on the ticket conversation.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Settings / Custom Fields toggle */}
                {teamFields.length > 0 && (
                  <div className="space-y-3 rounded-lg border border-border bg-card/60 p-3.5">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {team.name} Specific Fields
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {teamFields.map((f) => (
                        <div key={f.id} className={cn(f.type === 'checkbox' && 'col-span-2')}>
                          {f.type !== 'checkbox' && (
                            <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                              {f.label}
                              {f.required && <span className="text-destructive">*</span>}
                            </div>
                          )}
                          {f.type === 'select' ? (
                            <select
                              value={(customValues[f.key] as string) ?? ''}
                              onChange={(e) => setCustom(f.key, e.target.value)}
                              className="h-9 w-full rounded-lg border border-input bg-background px-2 text-xs outline-none focus:border-primary"
                            >
                              <option value="">Select…</option>
                              {f.options?.map((o) => <option key={o}>{o}</option>)}
                            </select>
                          ) : f.type === 'checkbox' ? (
                            <label className="flex cursor-pointer items-center gap-2 text-xs">
                              <input 
                                type="checkbox" 
                                checked={!!customValues[f.key]} 
                                onChange={(e) => setCustom(f.key, e.target.checked)} 
                                className="accent-primary" 
                              />
                              {f.label}{f.required && <span className="text-destructive">*</span>}
                            </label>
                          ) : (
                            <input
                              type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                              value={(customValues[f.key] as string) ?? ''}
                              onChange={(e) => setCustom(f.key, e.target.value)}
                              className="h-9 w-full rounded-lg border border-input bg-background px-2 text-xs outline-none focus:border-primary"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>

            {/* Modal Footer with Actions */}
            <div className="flex items-center justify-between border-t border-border px-5 sm:px-6 py-3 bg-muted/10 shrink-0">
              <div className="text-xs text-muted-foreground hidden sm:block">
                Tickets are monitored 24/7 by Enterprise IT Support
              </div>
              <div className="flex items-center gap-2.5 ml-auto">
                <button 
                  type="button"
                  onClick={onClose} 
                  disabled={isSubmitting}
                  className="rounded-xl border border-border px-4 py-2 text-xs sm:text-sm font-medium transition hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!picked || !subject.trim() || isSubmitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-xs sm:text-sm font-medium text-primary-foreground transition hover:brightness-110 shadow-md disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      <span>Creating & Dispatching...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" /> 
                      <span>Create Ticket</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
